import { Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { ChallengesStatuses, LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import {
  Modal,
  ModalConfig,
  ModalType,
  NonPersistedModalName,
  PersistedModalName,
  isModalConfig,
} from '@app/shared/models/modal.model';
import { Wallet } from '@app/shared/models/wallet.model';
import { AssetsService } from '@app/shared/services/assets.service';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { AppService } from '@app/app.service';
import { Observable } from 'rxjs';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { ProgressBarComponent } from '@app/shared/components/progress-bar/progress-bar.component';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { WalletRepository } from '@app/shared/state/wallet/wallet.repository';
import { Throttle } from '@app/shared/decorators/throttle.decorator';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-gamechallenges-modal',
  templateUrl: './gamechallenges.component.html',
  styleUrls: ['./gamechallenges.component.scss'],
  standalone: true,
  imports: [
    WalletComponent,
    BoxComponent,
    ProgressBarComponent,
    DisplayComponent,
    ButtonComponent,
    SharedModule,
    AnimatedTimerComponent,
  ],
})
export class GameChallengesModalComponent implements OnInit {
  componentDestroy: () => Observable<unknown>;
  NonPersistedModalName = NonPersistedModalName;
  PersistedModalName = PersistedModalName;
  gameIcon: unknown;
  challengeText: string;
  gameChallenge = signal<LimitedTimeEvent | null>(null);
  gameChallengeStatuses = signal<ChallengesStatuses | null>(null);
  eventId: string;

  bgImg: string;
  logoImg: string;
  boxImg: string;
  showCloseButton: boolean;
  showCompleted: boolean;
  actionButton: boolean;
  showTimer = signal(false);
  isRedeeming = signal(false);
  lang: string;

  private _gameChallengeCopy: LimitedTimeEvent;

  get wallet(): Wallet {
    return this.walletRepo.getWallet();
  }

  private readonly eventManager = inject(EventManager);
  private readonly logger = inject(LoggerService);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly translateService = inject(TranslateService);
  private readonly assetsService = inject(AssetsService);
  private readonly dialog = inject(MatDialog);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly appService = inject(AppService);
  private readonly walletRepo = inject(WalletRepository);

  gameChallengesModalData = inject<Modal | ModalConfig>(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.lang = this.translateService.currentLang;

    if (isModalConfig(this.gameChallengesModalData)) {
      this.eventId = this.gameChallengesModalData.properties.eventId;
    } else {
      const eventId = this.gameChallengesModalData.data.eventId
        ? this.gameChallengesModalData.data.eventId
        : this.gameChallengesModalData.data.properties.eventId;
      this.eventId = eventId;
    }

    this.eventsRepo.visibleLimitedTimeEvents$.pipe(takeUntil(this.componentDestroy())).subscribe(() => {
      const event = this.eventsRepo.getEventById(this.eventId);

      if (event) {
        this._gameChallengeCopy = event;
      }

      this.gameChallenge.set(this._gameChallengeCopy);
      this.gameChallengeStatuses.set(this._gameChallengeCopy.properties.challengestatuses[0]);
    });

    this.assetsService.gamesVersion$.subscribe({
      next: (versions) => {
        if (this.gameChallengeStatuses().isGeneric) return;

        const url = this.assetsService.getGameIconUrlLegacy(this.gameChallengeStatuses()!.gameId, versions);

        this.gameIcon = { 'background-image': `url('${url}')` };
      },
      error: (error) => this.logger.error(error),
    });

    this.manageChallengeText();

    setTimeout(() => {
      if (this.gameChallenge()?.endDate) {
        this.showTimer.set(true);
      }
    }, 500);
  }

  manageRedeem = async (): Promise<void> => {
    if (!this.gameChallengeStatuses()!.finished) {
      if (this.gameChallengeStatuses()!.isGeneric) {
        this.closeModal();
      } else {
        this.goToGame();
      }
    } else {
      this.isRedeeming.set(true);
      this.limitedTimeEventService
        .redeemGameChallenge(this.gameChallenge()!.eventId, this.gameChallengeStatuses()!.id)
        .pipe(finalize(() => this.isRedeeming.set(false)))
        .subscribe({
          next: (event: Partial<LimitedTimeEvent>) => {
            if (event) {
              this.limitedTimeEventService.updateLimitedTimeEventById(event);
            }
          },
          error: (error) => {
            this.logger.error(error);
          },
        });
    }
  };

  @Throttle(2000)
  goToGame(): void {
    this.eventManager.closeModal(
      new Modal(
        this.gameChallengesModalData.id,
        isModalConfig(this.gameChallengesModalData) ? ModalType.Persisted : ModalType.View,
        this.gameChallengesModalData,
        '',
        true
      )
    );

    this.dialog
      .getDialogById(this.gameChallengesModalData.id)
      .afterClosed()
      .subscribe({
        next: () => {
          this.appService.showGame(this.gameChallengeStatuses()!.gameId).catch((e) => this.logger.error(e));
        },
        error: (error) => this.logger.error(error),
      });
  }

  manageChallengeText(): void {
    this.challengeText = this.limitedTimeEventService.getGameChallengeTransaltionKey(
      this.gameChallenge()?.properties?.challengestatuses[0]
    );
  }

  closeModal(): void {
    this.eventManager.closeModal(
      new Modal(
        this.gameChallengesModalData.id,
        isModalConfig(this.gameChallengesModalData) ? ModalType.Persisted : ModalType.View,
        this.gameChallengesModalData
      )
    );
  }
}
